import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { IcontestUser } from 'app/features/contest/models/contest-api.models';
import { LeaderboardUserData } from 'app/features/contest/models/leaderboard-user-data';

type CustomField = {
  key: string;
  value: unknown;
};

const KNOWN_CONTEST_USER_FIELDS: ReadonlySet<string> = new Set<string>([
  'activity_id',
  'age',
  'chrono',
  'completion_percentage',
  'distance',
  'email',
  'first_name',
  'gender',
  'last_name',
  'ppr',
  'ranking',
  'start_date',
  'wpk',
]);

@Injectable({ providedIn: 'root' })
export class ContestMapperService {
  private readonly datePipe: DatePipe = inject(DatePipe);

  toLeaderboardUsers(rawUsersData: IcontestUser[]): LeaderboardUserData[] {
    return rawUsersData.map((rawUser: IcontestUser) => {
      const fullName: string =
        `${rawUser.first_name ?? ''} ${rawUser.last_name ?? ''}`.trim() || 'Anonymous';
      const userData: LeaderboardUserData = {
        ...(rawUser as unknown as LeaderboardUserData),
        full_name: fullName,
        highlight: false,
        id: rawUser.activity_id,
        position: rawUser.ranking ?? undefined,
      };
      const customField: CustomField | null = this.getCustomField(rawUser);

      if (customField) {
        userData.customField = customField.value;
        userData.customFieldKey = customField.key;
      }

      if (typeof rawUser.distance === 'number') {
        userData.distance = `${Math.round(rawUser.distance).toLocaleString('fr-FR')} m`;
      }

      if (rawUser.start_date) {
        const date: Date = new Date(rawUser.start_date * 1000);
        userData.date = this.datePipe.transform(date, 'dd MMM') ?? '-';
        userData.hour = this.datePipe.transform(date, 'HH:mm') ?? '-';
      }

      Object.keys(userData).forEach((key: string) => {
        const userDataKey = key as keyof typeof userData;
        if (userData[userDataKey] === undefined || userData[userDataKey] === null) {
          userData[userDataKey] = '-' as never;
        }
      });

      return userData;
    });
  }

  private getCustomField(rawUser: IcontestUser): CustomField | null {
    const customFieldKey: string | undefined = Object.keys(rawUser).find((key: string) => {
      if (KNOWN_CONTEST_USER_FIELDS.has(key)) return false;

      const value: unknown = rawUser[key];
      return value !== undefined && value !== null;
    });

    return customFieldKey ? { key: customFieldKey, value: rawUser[customFieldKey] } : null;
  }
}
